import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterDialog from '../components/RegisterDialog';
import { events as staticEvents } from '../siteContent';
import { useSiteContent } from '../context/SiteContentContext';
import { isAuthenticated } from '../utils/auth';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalize = (e) => ({
  id: e.id || e.title,
  title: e.title,
  date: e.event_date || e.date,
  category: e.category || e.tag,
  format: e.format,
  location: e.location || '',
  description: e.description || ''
});

const EventSkeleton = () => (
  <div className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden">
    <div className="h-36 bg-[#E5E7EB] animate-pulse" />
    <div className="p-6 space-y-3">
      <div className="h-4 w-28 bg-[#E5E7EB] animate-pulse rounded-full" />
      <div className="h-5 bg-[#E5E7EB] animate-pulse rounded-lg" />
      <div className="h-4 bg-[#E5E7EB] animate-pulse rounded-lg w-4/5" />
      <div className="h-9 bg-[#E5E7EB] animate-pulse rounded-full mt-4" />
    </div>
  </div>
);

const Events = () => {
  const { events: eventsCms } = useSiteContent();
  const [events, setEvents] = useState(staticEvents.map(normalize));
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const auth = isAuthenticated();

  useEffect(() => {
    axios.get(`${API}/events/`).then((r) => {
      if (r.data && r.data.length) setEvents(r.data.map(normalize));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ['All', ...Array.from(new Set(events.map((e) => e.category)))], [events]);
  const filtered = filter === 'All' ? events : events.filter((e) => e.category === filter);

  return (
    <div className="bg-[#FDFBF9]" data-testid="events-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#E74C3C] mb-4">{eventsCms.hero.eyebrow}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.05]">
            {eventsCms.hero.heading}
          </h1>
          <p className="mt-6 text-lg text-[#666] max-w-xl leading-relaxed">
            {eventsCms.hero.subtext}
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-[2rem] bg-[#003366] text-white p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full bg-[#E74C3C]/25 blur-2xl" />
            <CalendarDays className="w-10 h-10 text-[#E74C3C] mb-4 relative" />
            <p className="font-heading text-2xl font-bold relative">{eventsCms.clsCard.heading}</p>
            <p className="text-white/75 mt-3 relative">{eventsCms.clsCard.subtext}</p>
            <Link to="/corporate-life-series" className="relative">
              <Button className="mt-6 rounded-full bg-white text-[#003366] hover:bg-white/90 group">{eventsCms.clsCard.cta} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-y border-[#EAE5E0] bg-white sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex gap-2.5 overflow-x-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`event-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap border transition-colors ${filter === c ? 'bg-[#003366] border-[#003366] text-white' : 'bg-white border-[#EAE5E0] text-[#1A1A1A] hover:border-[#003366]'}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Auth banner for guests */}
      {!auth && (
        <div className="max-w-5xl mx-auto px-6 mt-8">
          <div className="rounded-2xl bg-[#003366] text-white p-5 flex flex-col sm:flex-row items-center gap-4 justify-between" data-testid="events-auth-banner">
            <p className="font-medium">Members can register for any event. <span className="text-white/70 font-normal text-sm">Join free to secure your spot.</span></p>
            <Link to="/membership" className="flex-shrink-0">
              <Button className="rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white px-5 text-sm">
                Join Free <ArrowRight className="ml-1.5 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Event grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((n) => <EventSkeleton key={n} />)}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((e) => {
                const parts = (e.date || '').split(' ');
                const day = parts[0] || '';
                const month = parts[1] || '';
                return (
                  <div key={e.id} className="bg-white rounded-3xl border border-[#EAE5E0] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col" data-testid="events-page-card">
                    {/* Date visual header */}
                    <div className="h-36 bg-[#003366] flex flex-col items-center justify-center flex-shrink-0 relative overflow-hidden">
                      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-[#E74C3C]/20 blur-xl" />
                      <span className="font-heading font-bold text-5xl text-white leading-none relative">{day}</span>
                      <span className="text-sm uppercase tracking-widest text-white/60 mt-1 relative">{month}</span>
                    </div>
                    {/* Content */}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-semibold uppercase tracking-widest text-[#E74C3C] bg-[#E74C3C]/10 rounded-full px-2.5 py-0.5">{e.category}</span>
                        <span className="text-xs text-[#999]">·</span>
                        <span className="text-xs text-[#666]">{e.format}</span>
                      </div>
                      <h3 className="font-heading text-lg font-bold text-[#1A1A1A] leading-snug line-clamp-2">{e.title}</h3>
                      {e.description && <p className="text-sm text-[#666] mt-2.5 line-clamp-2 flex-1">{e.description}</p>}
                      {e.location && (
                        <span className="text-xs text-[#999] flex items-center gap-1 mt-2">
                          <MapPin className="w-3 h-3" />{e.location}
                        </span>
                      )}
                      <div className="mt-5">
                        {auth ? (
                          <RegisterDialog
                            kind="event"
                            refId={e.id}
                            title={e.title}
                            label="Register — Free"
                            testid="events-page-register-btn"
                            triggerClassName="w-full rounded-full bg-[#E74C3C] hover:bg-[#C0392B] text-white font-medium"
                          />
                        ) : (
                          <Link to="/membership" data-testid="events-join-btn">
                            <Button variant="outline" className="w-full rounded-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white">
                              Join Free to Register
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;
